import React from 'react';
import { Autocomplete, Box, Checkbox, CircularProgress, FormControlLabel, MenuItem, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { darken } from '@mui/material/styles';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import { isHourBasedFrequency } from '../../../utils/frequencyConfig';
import Modal from '../modal';
import { DAYS, WEEKS, MONTHS, PrescriptionDuration, isDurationChosen, normaliseDuration } from './prescriptionDuration';

/**
 * SHER-567 — "Fréquemment prescrits": add several medications to a prescription in one action.
 *
 * The list is personal, end to end (SHER-869): it is what THIS clinician has prescribed in
 * this investigation, most-prescribed first, with the dose and frequency they have actually
 * used offered under each drug. The pharmacy is not the list — it only decorates the drugs it
 * happens to carry, with a stock-out mark. It used to BE the list, which meant everything
 * prescribed off the formulary was missing and the whole inventory was present.
 *
 * Presentational on purpose: it receives its items and hands back the picked drugs. Fetching
 * the history, joining the pharmacy to it and turning drugs into prescription rows all belong
 * to the caller, which is what keeps this out of the smart-field dispatcher (already past 1000
 * lines) and lets the catalog exercise every state without a store.
 */

/**
 * One way this clinician has prescribed this drug before, with how often.
 *
 * Measured on the CHOM production data (2.888 prescription lines, 33 prescribers,
 * 2024-01 → 2026-08): 89% of a clinician's lines for a given drug fall on their own most-used
 * dose+frequency, which is why their history leads. But 45% of (clinician, drug) pairs use
 * more than one, which is why it can never be the only way in.
 *
 * Duration IS part of this, since SHER-1029. It was left out on one tenant's numbers (2 of 34
 * clinician-drug pairs repeating a duration), which is not enough to settle a product question:
 * a reusable prescribing entry carrying the whole dosing statement is what the field ships, and
 * selection from pre-populated preference lists is measured as safety-positive. The server
 * derives it from the dates, and a null end date is the chronic answer rather than a missing
 * one — see getPrescriptionHistoryItems.
 */
export interface PosologySuggestion {
    dose: string;
    frequency: string;
    /** Times this clinician prescribed it this way. Orders the list; never displayed (SHER-1029). */
    timesUsed?: number;
    /**
     * SHER-1029 — how long this clinician ran this posology, derived server-side from the
     * treatment-start/treatment-finish pair they actually prescribed with. Picking the
     * suggestion answers the duration, so the boxes are only needed off-list.
     *
     * Optional in the type, required in practice: a server that does not send one leaves the
     * suggestion unusable, and `ranked` drops it rather than offering a row the clinician
     * cannot complete. That is the fail-closed direction — the alternative is a suggestion
     * that opens nothing and a confirm that never enables.
     */
    duration?: PrescriptionDuration;
}

export interface PharmacyStockItem {
    /**
     * The drug's identity in this panel. A drug the pharmacy does not carry has no id, so the
     * key is its name — which is what the server aggregates the history by (SHER-869).
     */
    key: string;
    name: string;
    /** What the prescription row's `drug-code` carries. Decided server-side — see SHER-869. */
    drugCode: string;
    /** False when the ward has none today. Marked, never blocked — see onConfirm. */
    inStock: boolean;
    /** This clinician's own history for this drug. Absent for one they have never prescribed. */
    posologies?: PosologySuggestion[];
    /** Times this clinician has prescribed the item. Sorts the list only — never displayed. */
    usageCount?: number;
}

/** A slave field's own `survey_field_option` list — the closed vocabulary treatment forms use. */
export interface PosologyOption {
    value: string;
    label: string;
}

/**
 * A drug the clinician picked, plus the posology the row cannot be saved without.
 *
 * Carrying it here is a safety requirement, not a convenience. Nothing downstream validates a
 * prescription row: `saveDrug()` guards the sub-form, `elementSelected`
 * (SmartFields/index.tsx:863) appends to the list unchecked, and SectionForm passes treatment
 * fields to submit unvalidated. A row handed over bare is therefore not rejected — it is
 * persisted as a prescription with no dose and no frequency.
 */
export interface PickedDrug {
    name: string;
    drugCode: string;
    dose: string;
    frequency: string;
    /**
     * How long the treatment runs, or an explicit chronic declaration. Required for the same
     * reason dose and frequency are: nothing downstream validates a prescription row, and a row
     * handed over without it is persisted with no end date — which reads as "chronique / à long
     * terme" on every screen and on the printed ordonnance (SHER-834).
     */
    duration: PrescriptionDuration;
}

interface Posology {
    dose: string;
    frequency: string;
}

/** Which row of the posology list is chosen: an index into the history, or the empty boxes. */
type Choice = number | 'other';

/**
 * "Is this item ticked, and if so what posology did it resolve to" is one fact per item, not
 * two independent maps — `choice` is undefined until the clinician picks a radio option (or is
 * pre-set to `'other'` when there is no history to offer), `typed` always starts as
 * `EMPTY_POSOLOGY` on selection. Keeping both under a single key means there is exactly one
 * setState call per user action instead of updaters coordinating each other as side effects.
 */
interface Selection {
    choice: Choice | undefined;
    typed: Posology;
    duration: PrescriptionDuration;
}

const EMPTY_POSOLOGY: Posology = { dose: '', frequency: '' };
/** Nothing declared yet: neither branch of "duration or chronic" is filled in, so confirm stays shut. */
const EMPTY_DURATION: PrescriptionDuration = { chronic: false, amount: null, unit: null };

/** What each unanswered half of a row is called on screen. Keyed by missingFrom()'s result. */
const MISSING_MESSAGE = {
    posology: 'hospital.pharmacy-stock-picker.posology-required',
    duration: 'hospital.pharmacy-stock-picker.duration-required',
} as const;

/** Tells two pickers mounted at once apart, so their rows never share a DOM id (the catalog page). */
let pickerInstances = 0;

function isComplete(p: Posology | undefined): boolean {
    return !!p && p.dose.trim() !== '' && p.frequency.trim() !== '';
}

/**
 * Most-used first, whatever order the caller sent. Array.sort is stable, so an unranked list
 * keeps its order.
 *
 * A suggestion whose duration is missing or half-filled is dropped (SHER-1029): picking it could
 * not answer the duration, and the boxes that would have rescued it now live behind "Another
 * posology". Dropping it sends the clinician down that path, which is the decision taken for the
 * case where the history has no duration to offer. Drop every suggestion for a drug and the row
 * opens the empty boxes on its own — see `toggle`.
 */
function ranked(item: PharmacyStockItem): PosologySuggestion[] {
    return [...(item.posologies ?? [])]
        .filter((p) => isDurationChosen(p.duration))
        .sort((a, b) => (b.timesUsed ?? 0) - (a.timesUsed ?? 0));
}

interface Props extends LocalizeContextProps {
    open: boolean;
    items: PharmacyStockItem[];
    loading?: boolean;
    /**
     * The `treatment-dose`/`treatment-frecuency` slave fields' own options — the exact same
     * vocabulary SingleTreatmentSelector's manual "Add Treatment" form is built from. Handed
     * a bare TextField, a clinician could type anything into dose or frequency; this panel
     * exists to be the trustworthy, guided path onto the pharmacy's own list, so it cannot be
     * looser than the manual form it sits beside.
     */
    doseOptions: PosologyOption[];
    frequencyOptions: PosologyOption[];
    onClose: () => void;
    onConfirm: (drugs: PickedDrug[]) => void;
}

const PharmacyStockPicker: React.FC<Props> = ({ open, items, loading = false, doseOptions, frequencyOptions, onClose, onConfirm, translate }) => {
    const [selection, setSelection] = React.useState<Record<string, Selection>>({});

    // The container renders this component in place (open toggles a prop, not a mount/unmount)
    // so its own fetch cache survives between opens — see PharmacyStockPickerContainer's
    // docstring. That same persistence meant a ticked selection survived too: closing (Cancel,
    // X, or after a successful confirm) and reopening showed the same rows still ticked.
    React.useEffect(() => {
        if (!open) {
            setSelection({});
        }
    }, [open]);

    // Most-prescribed first, alphabetical among the rest. The server already returns them in
    // this order; re-applying it here keeps the panel correct whatever a caller hands it. No
    // separate "favourites" list: the field norm (PCC eRx) surfaces favourites inside the same
    // search rather than a curated, separately-saved one — this is that norm applied to sort
    // order, not a second list.
    const sorted = React.useMemo(
        () => [...items].sort((a, b) =>
            (b.usageCount ?? 0) - (a.usageCount ?? 0) || a.name.localeCompare(b.name)
        ),
        [items],
    );

    // Lazy initialiser rather than a ref written during render: it runs exactly once per
    // instance, types as a plain string, and keeps the module counter out of the render body.
    const [idPrefix] = React.useState(() => `pharmacy-stock-picker-${pickerInstances++}`);

    /**
     * A stable, whitespace-free id per row, for the row's controls to point `aria-describedby`
     * at. The row's own key is a drug name — spaces, commas, slashes — and `aria-describedby`
     * takes a space-separated LIST of ids, so the key can never be the id raw.
     * `encodeURIComponent` is injective and emits no ASCII whitespace, so it is collision-free
     * where a sanitiser would only be unique by luck ("Augmentin 1 g/200 mg" and
     * "Augmentin 1 g,200 mg" sanitise alike). Deriving from the key rather than from list
     * position also means a reorder cannot silently re-key a row that is already ticked.
     */
    const hintIdOf = (item: PharmacyStockItem): string =>
        `${idPrefix}-missing-${encodeURIComponent(item.key)}`;

    /** What the row would be prescribed as, or undefined while nothing is chosen yet. */
    function posologyOf(item: PharmacyStockItem): Posology | undefined {
        const choice = selection[item.key]?.choice;
        if (choice === undefined) {
            return undefined;
        }
        if (choice === 'other') {
            return selection[item.key].typed;
        }
        const suggestion = ranked(item)[choice];
        return suggestion && { dose: suggestion.dose, frequency: suggestion.frequency };
    }

    function toggle(item: PharmacyStockItem) {
        const id = item.key;
        setSelection((current) => {
            const next = { ...current };
            if (id in next) {
                // Unticking discards what was chosen or typed, so a posology can never
                // survive onto a row the clinician did not mean it for.
                delete next[id];
            } else if (ranked(item).length === 0) {
                // No history to offer: the empty boxes are the only way in, so open them
                // rather than making the clinician ask for the only option there is.
                next[id] = { choice: 'other', typed: { ...EMPTY_POSOLOGY }, duration: { ...EMPTY_DURATION } };
            } else {
                next[id] = { choice: undefined, typed: { ...EMPTY_POSOLOGY }, duration: { ...EMPTY_DURATION } };
            }
            return next;
        });
    }

    function setTypedField(id: string, field: keyof Posology, value: string) {
        setSelection((all) => ({
            ...all,
            [id]: {
                choice: all[id]?.choice,
                typed: { ...(all[id]?.typed ?? EMPTY_POSOLOGY), [field]: value },
                duration: all[id]?.duration ?? { ...EMPTY_DURATION },
            },
        }));
    }

    function setDuration(id: string, patch: Partial<PrescriptionDuration>) {
        setSelection((all) => ({
            ...all,
            [id]: {
                choice: all[id]?.choice,
                typed: all[id]?.typed ?? EMPTY_POSOLOGY,
                duration: { ...(all[id]?.duration ?? EMPTY_DURATION), ...patch },
            },
        }));
    }

    const selectedItems = sorted.filter((i) => i.key in selection);

    /**
     * The one place that answers "what is this row still waiting for?". Both the confirm gate
     * and the row's own message read it, so what the picker enforces and what it says it is
     * enforcing cannot drift apart — which is the exact failure this ticket exists to fix.
     *
     * It names whichever half is open rather than both, because they are answered in that
     * order: the posology sits above the duration, so a bare row asks for the first thing to
     * do rather than for everything at once.
     */
    function missingFrom(item: PharmacyStockItem): 'posology' | 'duration' | null {
        if (!isComplete(posologyOf(item))) {
            return 'posology';
        }
        if (!isDurationChosen(selection[item.key]?.duration)) {
            return 'duration';
        }
        return null;
    }

    /**
     * The id of this row's "still waiting for" message, for its controls to describe themselves
     * with — and nothing once the row is answered, so no control is left pointing at an id that
     * has just left the DOM. Reads `missingFrom` like everything else, so the association can
     * never outlive, or fall short of, the message it is for.
     */
    function hintFor(item: PharmacyStockItem): string | undefined {
        return missingFrom(item) ? hintIdOf(item) : undefined;
    }

    // A duration is as required as the dose: see PickedDrug.duration. Confirm stays disabled
    // until every ticked row says how long it runs, or says it is chronic.
    const allComplete = selectedItems.length > 0 && selectedItems.every((i) => missingFrom(i) === null);

    function confirm() {
        if (!allComplete) {
            return;
        }
        // Emitted in list order rather than click order, so the rows land in the same
        // sequence the clinician just read them in.
        onConfirm(
            selectedItems.map((i) => ({
                name: i.name,
                drugCode: i.drugCode,
                ...(posologyOf(i) ?? EMPTY_POSOLOGY),
                duration: selection[i.key]?.duration ?? { ...EMPTY_DURATION },
            })),
        );
    }

    /**
     * Dose and frequency, rendered with the SAME two controls SingleTreatmentSelector's own
     * "Add Treatment" form uses on `treatment-dose`/`treatment-frecuency`: dose is a `freeSolo`
     * Autocomplete (real prescriptions carry fractional/free doses a fixed list can't predict —
     * "1/2", "2.5" — so the manual form itself doesn't lock it down), frequency is a closed
     * Autocomplete grouped hour-based vs standard, because "how often" IS a fixed, small,
     * clinically-meaningful set and the manual form never lets it be free text either.
     */
    function renderBoxes(item: PharmacyStockItem) {
        const posology = selection[item.key]?.typed ?? EMPTY_POSOLOGY;
        return (
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <Autocomplete
                    size="small"
                    freeSolo
                    fullWidth
                    options={doseOptions}
                    value={posology.dose}
                    inputValue={posology.dose}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : translate(option.label).toString())}
                    onInputChange={(_event, value) => setTypedField(item.key, 'dose', value)}
                    onChange={(_event, value) => setTypedField(item.key, 'dose', value == null ? '' : (typeof value === 'string' ? value : value.value))}
                    renderInput={(params) => (
                        <TextField {...params} variant="outlined" label={<Translate id="hospital.pharmacy-stock-picker.dose" />}
                            inputProps={{ ...params.inputProps, 'data-testid': `posology-dose-${item.key}`, 'aria-describedby': hintFor(item) }} />
                    )}
                />
                <Autocomplete
                    size="small"
                    fullWidth
                    options={frequencyOptions}
                    groupBy={(option) => (isHourBasedFrequency(option.value)
                        ? translate('hospital.frecuency-group-hour-based').toString()
                        : translate('hospital.frecuency-group-standard').toString())}
                    getOptionLabel={(option) => translate(option.label).toString()}
                    value={frequencyOptions.find((option) => option.value === posology.frequency) ?? null}
                    onChange={(_event, value) => setTypedField(item.key, 'frequency', value?.value ?? '')}
                    renderInput={(params) => (
                        <TextField {...params} variant="outlined" label={<Translate id="hospital.pharmacy-stock-picker.frequency" />}
                            inputProps={{ ...params.inputProps, 'data-testid': `posology-frequency-${item.key}`, 'aria-describedby': hintFor(item) }} />
                    )}
                />
            </Box>
        );
    }

    /**
     * How long the treatment runs, offering the same two ways out as the manual sub-form: an
     * amount + unit, or the "chronique" tick that means no end date. Nothing is preselected —
     * a default here would put the picker back to asserting a duration nobody chose, which is
     * the whole of SHER-834.
     */
    function renderDuration(item: PharmacyStockItem) {
        const duration = selection[item.key]?.duration ?? EMPTY_DURATION;
        // Both boxes must tell assistive technology the same thing, and the test asserts them
        // in lockstep, so it is one invariant with one source rather than two copies that drift.
        //
        // `required` and the asterisk are both sighted-only (SHER-922): MUI 5.14 lands the prop
        // on SelectNativeInput and the asterisk on a span, and renders BOTH aria-hidden, while
        // putting no aria-required on the `role="combobox"` element it actually exposes.
        // SelectDisplayProps is what reaches that element, so the requirement and the row's
        // message have to travel through it.
        const durationA11y = {
            SelectDisplayProps: { 'aria-required': !duration.chronic, 'aria-describedby': hintFor(item) },
        };
        return (
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                <TextField
                    select
                    size="small"
                    // 90 clipped the label to "Durat..", which hid the required marker the
                    // confirm actually enforces; 120 fits "Duration *" in all three languages.
                    sx={{ minWidth: 120 }}
                    variant="outlined"
                    disabled={duration.chronic}
                    required={!duration.chronic}
                    SelectProps={durationA11y}
                    label={<Translate id="hospital.treatment-duration" />}
                    value={duration.amount ?? ''}
                    onChange={(e) => setDuration(item.key, { amount: e.target.value === '' ? null : Number(e.target.value) })}
                    inputProps={{ 'data-testid': `duration-amount-${item.key}` }}
                >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                        <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    size="small"
                    sx={{ minWidth: 130 }}
                    variant="outlined"
                    disabled={duration.chronic}
                    required={!duration.chronic}
                    SelectProps={durationA11y}
                    label={<Translate id="hospital.time-unit" />}
                    value={duration.unit ?? ''}
                    onChange={(e) => setDuration(item.key, { unit: e.target.value === '' ? null : e.target.value })}
                    inputProps={{ 'data-testid': `duration-unit-${item.key}` }}
                >
                    {[DAYS, WEEKS, MONTHS].map((unit) => (
                        <MenuItem key={unit} value={unit}>
                            <Translate id={`hospital.time-unit-options.${unit}`} />
                        </MenuItem>
                    ))}
                </TextField>
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={duration.chronic}
                            // Ticking chronic clears the boxes: leaving a half-typed duration
                            // behind a disabled control is state the clinician can no longer see.
                            onChange={(e) => setDuration(item.key, e.target.checked
                                ? { chronic: true, amount: null, unit: null }
                                : { chronic: false })}
                            inputProps={{ 'data-testid': `duration-chronic-${item.key}` } as any}
                        />
                    }
                    label={
                        <Typography variant="body2" component="span">
                            <Translate id="hospital.chronic" />
                        </Typography>
                    }
                />
            </Box>
        );
    }

    /**
     * What this ticked row is still waiting for, in the row itself (SHER-918).
     *
     * The confirm is disabled until the posology AND the duration are answered, and until
     * this it said so nowhere: the duration boxes carried no required marker, and a greyed
     * "Ajouter" with no message reads as a broken button rather than an unanswered question.
     * That is how it was reported from production, twice. The manual sub-form beside it has
     * always named its missing duration (SingleTreatmentSelector.jsx) — it just does it after
     * a rejected save, which a disabled confirm never gives the clinician the chance to hit.
     *
     * It names whichever half is open rather than listing both, because they are answered in
     * that order: the posology sits above the duration, and a row with neither should ask for
     * the first thing to do, not for everything at once.
     */
    function renderMissing(item: PharmacyStockItem) {
        const missing = missingFrom(item);
        return (
            // The live region is rendered for every ticked row, message or not (SHER-922). A
            // role="status" that arrives already holding its text is not reliably announced —
            // the region has to be in the DOM BEFORE the text changes — and the change is the
            // case that matters here: the posology gets answered and the row starts asking for
            // the duration instead, with focus nowhere near this line.
            <Box role="status">
                {missing && (
                    <Typography
                        variant="caption"
                        component="p"
                        id={hintIdOf(item)}
                        // MUI's default warning.dark (#e65100 — src/theme/ overrides no warning
                        // palette) measures 3.79:1 on the panel's white paper, under the 4.5:1
                        // AA asks of 12px text. This is the colour MUI itself renders warning
                        // text in on a light theme, so it stays a theme value rather than a hex
                        // pasted here, keeps the amber "unanswered, not failed" reading that
                        // error.main would lose, and measures 9.46:1. Pinned by a test that
                        // measures what renders.
                        sx={{ mt: 0.5, color: (theme) => darken(theme.palette.warning.light, 0.6) }}
                        data-testid={`incomplete-${item.key}`}
                    >
                        <Translate id={MISSING_MESSAGE[missing]} />
                    </Typography>
                )}
            </Box>
        );
    }

    /**
     * The duration inside the suggestion's own label (SHER-1029).
     *
     * Without this the fast path answers the duration invisibly: the clinician picks
     * "1 · 24h", the confirm opens, and they have committed to a length they were never shown.
     * The boxes cannot do this job any more — they moved behind "Another regimen" — so the
     * suggestion has to say its whole regimen, which is what makes it a reusable prescription
     * rather than a posology with something hidden behind it.
     *
     * Normalised here as well as on adoption, so what is read and what is committed are the same
     * value rather than two conversions that could drift.
     */
    function renderSuggestedDuration(suggestion: PosologySuggestion) {
        const duration = normaliseDuration(suggestion.duration);
        if (duration.chronic) {
            // Its own short label, not the checkbox's: "chronique / à long terme" mid-line pushes
            // the regimen onto a second row for the commonest case there is.
            return <Translate id="hospital.pharmacy-stock-picker.chronic-short" />;
        }
        // Fixed abbreviations rather than the dropdown's full words, so no unit has to agree with
        // a number: "1 wk" and "3 wk" are both right, where "1 weeks" never was.
        return <>{duration.amount} <Translate id={`hospital.pharmacy-stock-picker.unit-short.${duration.unit}`} /></>;
    }

    function renderPosology(item: PharmacyStockItem) {
        const suggestions = ranked(item);
        if (suggestions.length === 0) {
            // Nothing to offer, so the manual path is the only path and it carries its own
            // duration boxes — `toggle` has already put this row on 'other'.
            return (
                <>
                    {renderBoxes(item)}
                    {renderDuration(item)}
                </>
            );
        }
        return (
            <RadioGroup
                data-testid={`posology-choices-${item.key}`}
                aria-describedby={hintFor(item)}
                value={selection[item.key]?.choice ?? ''}
                onChange={(e) => setSelection((all) => {
                    const choice: Choice = e.target.value === 'other' ? 'other' : Number(e.target.value);
                    // SHER-1029: the suggestion IS the answer to the duration, so picking one
                    // adopts its own. Going off-list clears it instead of inheriting the last
                    // suggestion's — a duration the clinician can no longer see is exactly the
                    // unasked value SHER-834 removed.
                    const duration = choice === 'other'
                        ? { ...EMPTY_DURATION }
                        // Said in the panel's own units — the server can only derive days, and
                        // "90 days" where the prescriber meant three months is the misreading
                        // this ticket is about.
                        : normaliseDuration(suggestions[choice]?.duration);
                    return {
                        ...all,
                        [item.key]: {
                            choice,
                            typed: all[item.key]?.typed ?? EMPTY_POSOLOGY,
                            duration,
                        },
                    };
                })}
            >
                {suggestions.map((suggestion, index) => (
                    <FormControlLabel
                        key={`${suggestion.dose}-${suggestion.frequency}-${index}`}
                        value={index}
                        control={<Radio size="small" inputProps={{ 'data-testid': `posology-option-${item.key}-${index}` } as any} />}
                        label={
                            <Typography variant="body2" component="span">
                                {suggestion.dose} · {suggestion.frequency} · {renderSuggestedDuration(suggestion)}
                            </Typography>
                        }
                    />
                ))}
                <FormControlLabel
                    value="other"
                    control={<Radio size="small" inputProps={{ 'data-testid': `posology-option-${item.key}-other` } as any} />}
                    label={
                        <Typography variant="body2" component="span">
                            <Translate id="hospital.pharmacy-stock-picker.other" />
                        </Typography>
                    }
                />
                {selection[item.key]?.choice === 'other' && (
                    <>
                        {renderBoxes(item)}
                        {renderDuration(item)}
                    </>
                )}
            </RadioGroup>
        );
    }

    return (
        <Modal
            open={open}
            closeModal={onClose}
            title={<Translate id="hospital.pharmacy-stock-picker.title" />}
            confirmAction={confirm}
            confirmButtonLabel="hospital.pharmacy-stock-picker.add"
            disabled={!allComplete}
            maxWidth="sm"
            fullWidth
        >
            <Box sx={{ width: '100%' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }} data-testid="pharmacy-stock-loading">
                        <CircularProgress size={28} />
                    </Box>
                ) : sorted.length === 0 ? (
                    <Typography variant="body2" sx={{ py: 3 }} data-testid="pharmacy-stock-empty">
                        <Translate id="hospital.pharmacy-stock-picker.empty" />
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            maxHeight: 320,
                            overflowY: 'auto',
                            pr: 1,
                            // Bounded and visibly scrollable: without this the list just stops
                            // mid-item and the helper below reads as one more row.
                            border: '1px solid rgba(0, 0, 0, 0.12)',
                            borderRadius: 1,
                            px: 1,
                        }}
                        data-testid="pharmacy-stock-list"
                    >
                        {sorted.map((item) => (
                            <Box key={item.key} sx={{ display: 'block' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name={item.name}
                                            checked={item.key in selection}
                                            onChange={() => toggle(item)}
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" component="span">
                                            {item.name}
                                            {!item.inStock && (
                                                <Typography
                                                    variant="caption"
                                                    component="span"
                                                    sx={{ ml: 1, opacity: 0.7 }}
                                                    data-testid="pharmacy-stock-out"
                                                >
                                                    (<Translate id="hospital.pharmacy-stock-picker.out-of-stock" />)
                                                </Typography>
                                            )}
                                        </Typography>
                                    }
                                />
                                {item.key in selection && (
                                    <Box sx={{ ml: 4, mb: 1 }}>
                                        {renderPosology(item)}
                                        {renderMissing(item)}
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default withLocalize(PharmacyStockPicker);
